import {
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Grid,
    Typography,
} from "@mui/material";
import { useRef } from "react";
import Barcode from "react-barcode";

type LabelOptions = {
    showName: boolean;
    showPrice: boolean;
    showSKU: boolean;
    showBarcode: boolean;
};

type VariantItem = {
    id: number;
    product_name: string;
    variant_name: string;
    sku: string;
    unit_cost: string;
    price: string;
    product_image_url?: string;
    variant_image_url?: string;
};

type Props = {
    items: VariantItem[];
    options: LabelOptions;
    onClose: () => void;
    onOptionsChange: (opts: LabelOptions) => void;
};

const LabelPrintModal: React.FC<Props> = ({
    items,
    options,
    onClose,
    onOptionsChange,
}) => {
    const printRef = useRef<HTMLDivElement>(null);

    const handleCheckboxChange = (key: keyof LabelOptions) => {
        onOptionsChange({ ...options, [key]: !options[key] });
    };

    const handlePrint = () => {
        const printContents = printRef.current?.innerHTML;
        const printWindow = window.open("", "", "height=600,width=800");
        if (printWindow && printContents) {
            printWindow.document.write("<html><head><title>Print Labels</title>");
            printWindow.document.write(
                `<style>
          body { font-family: Arial, sans-serif; padding: 10px; }
          .label { border: 1px solid #ccc; padding: 10px; margin: 8px; display: inline-block; width: 200px; height: 120px; vertical-align: top; text-align: center; }
          .barcode svg { width: 100%; height: 40px; }
          .label p { margin: 4px 0; font-size: 13px; }
          .price { font-weight: bold; font-size: 16px; margin-top: 5px; }
        </style>`
            );
            printWindow.document.write("</head><body>");
            printWindow.document.write(printContents);
            printWindow.document.write("</body></html>");
            printWindow.document.close();
            printWindow.focus();
            setTimeout(() => printWindow.print(), 500);
        }
    };

    return (
        <Dialog open onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>Print Labels</DialogTitle>
            <DialogContent>
                <Box mb={2}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={options.showPrice}
                                onChange={() => handleCheckboxChange("showPrice")}
                            />
                        }
                        label="Show price"
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={options.showName}
                                onChange={() => handleCheckboxChange("showName")}
                            />
                        }
                        label="Show name"
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={options.showBarcode}
                                onChange={() => handleCheckboxChange("showBarcode")}
                            />
                        }
                        label="Show barcode"
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={options.showSKU}
                                onChange={() => handleCheckboxChange("showSKU")}
                            />
                        }
                        label="Show SKU"
                    />
                </Box>

                <Box ref={printRef}>
                    {items.map((item) => (
                        <Box key={item.id} className="label">
                            {options.showBarcode && (
                                <div className="barcode">
                                    <Barcode
                                        value={item.sku}
                                        format="CODE128"
                                        height={60}
                                        width={0.8}
                                        displayValue={false}
                                    />
                                </div>
                            )}
                            {options.showSKU && <p>{item.sku}</p>}
                            {options.showName && (
                                <p>{`${item.product_name} / ${item.variant_name}`}</p>
                            )}
                            {options.showPrice && (
                                <p className="price">${parseFloat(item.price).toFixed(2)}</p>
                            )}
                        </Box>
                    ))}
                </Box>
            </DialogContent>

            <DialogActions>
                <Button onClick={handlePrint} variant="contained">
                    Print
                </Button>
                <Button onClick={onClose} variant="text">
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default LabelPrintModal;
